var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { convertToNumber3, FacetVisibility, LinkToModelFacet, RayCurveTeleporterFacet, RenderVisibilityMessage, ControllerButtonMessage, ControllerHand, AxisType, ControllerAxisMessage, ButtonType, ButtonSignal, RemoveSubModelMessage, degToRad } from "flavr-common";
import { Vector2, Vector3 as MathVector3, Quaternion, Euler, Matrix4 } from "flavr-math";
import { ControllerMovedMessage } from "@messages/controller/ControllerMessages";
import { DestinationReachedMessage, SetPlayerWorldTransformMessage, TeleportResultingViewDirectionMessage, EnableTeleportMessage, StartTeleportVrMessage } from "@messages/locomotion/LocomotionMessages";
import { renderManager } from "@modules/xr/RenderManager";
import { clockService } from "@modules/xr/ClockService";
import { modelPool } from "@modules/model";
import { NO_MESSAGING } from "@modules/model/types";
import { NewModule } from "@modules/module/NewModule";
import { FacetHandler, FacetRemoveHandler, MessageHandler } from "@modules/module";
import { preferencesService } from "@modules/preferences/PreferencesService";
import { camera } from "@modules/rendering/camera";
import { getControllerPickupTargetUid } from "@modules/inputs/controller";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { Teleporter } from "./Teleporter";
const TELEPORTER_REACTIVATION_DELAY_MS = 1000;
const TELEPORTER_INPUT_TIMEOUT_MS = 1000;
const Y_AXIS = new MathVector3(0, 1, 0);
const tempTeleportOffset = new MathVector3();
const tempTeleportTarget = new MathVector3();
const tempTeleportRotation = new Quaternion();
const TELEPORTER_LINK_OFFSET = new Matrix4().compose(new MathVector3(0, 0, 0), new Quaternion().setFromEuler(new Euler(degToRad(45), 0, 0, "XYZ")), new MathVector3(1, 1, 1)).toArray();
/**
 * Controls ray-curve teleporters for XR locomotion so controller input can preview valid teleport targets and move the player with the configured orientation rules.
 *
 * Facets:
 * - `RayCurveTeleporterFacet` - Defines teleporter models and their target arrow configuration.
 * - `FacetVisibility` - Enables or disables individual teleporter visuals at the facet level.
 *
 * Messages:
 * - `ControllerButtonMessage` and `ControllerAxisMessage` - Drive teleporter activation and joystick-based teleport gestures depending on the configured VR movement mode. `ControllerMovedMessage` is also handled but currently a no-op.
 * - `EnableTeleportMessage`, `TeleportResultingViewDirectionMessage` and `StartTeleportVrMessage` - Configure teleport availability, update the final facing direction and trigger teleports programmatically.
 * - `RenderVisibilityMessage` and `RemoveSubModelMessage` - Hide teleporters or remove their state when the underlying teleporter models disappear.
 */
export class TeleporterModule extends NewModule {
    /**
     * Enables teleporter activation by default for both controller hands.
     */
    constructor() {
        super("TeleporterModule");
        this.teleporter = new Map();
        this.teleporterActivation = new Map();
        this.teleporterUpdateInProgress = false;
        this.nextTeleporterActivationAllowedAt = 0;
        this.lastTeleportAxisInputAt = 0;
        this.teleporterActive = false;
        this.isTeleporting = false;
        this.joyStickValue = new Vector2(0, 0);
        this.vrMovementMode = "walk+teleport";
        this.onUpdate = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const dt = clockService.getDeltaToLastFrameInSeconds();
            for (const [uid, teleporter] of this.teleporter) {
                if (!teleporter.teleporter.isEnabled())
                    continue;
                const targetModelUid = getControllerPickupTargetUid(teleporter.controllerUid);
                const model = (_a = modelPool.getModel(targetModelUid)) !== null && _a !== void 0 ? _a : modelPool.getModel(uid);
                if (model !== undefined) {
                    teleporter.teleporter.position.copy(model.getWorldPosition());
                    teleporter.teleporter.direction.set(0, 0, -1).applyQuaternion(model.getWorldOrientation()).normalize();
                    const hasHit = yield teleporter.teleporter.intersectParabolicCurveWithNavMesh(dt);
                    teleporter.teleporter.setHitMarkerVisible(hasHit);
                }
            }
            this.enforceTeleporterInactivityTimeout();
        });
        this.teleporterActivation.set(ControllerHand.LeftHand, true);
        this.teleporterActivation.set(ControllerHand.RightHand, true);
    }
    /**
     * Loads the preferred VR movement mode ("walk", "teleport" or "walk+teleport") and keeps it in sync with
     * later preference changes.
     */
    onInit() {
        // Load initial preferences from settings
        this.vrMovementMode = preferencesService.getPreferredValue("vrMovementMode", "walk+teleport");
        // Listen for preference changes in settings
        preferencesService.onPreferenceChanged.add((preferenceName, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (preferenceName === "vrMovementMode") {
                this.handleMovementModeChanged(newValue);
            }
            return true;
        }));
    }
    /// Life cycle methods ///
    /**
     * Advances the teleporter preview every frame: updates each enabled teleporter's position/direction from
     * its target model, re-intersects its parabolic curve with the nav mesh, and enforces the input-inactivity
     * timeout.
     */
    onPreRender() {
        if (this.teleporterUpdateInProgress)
            return;
        this.teleporterUpdateInProgress = true;
        void this.updateTeleporterPreview();
    }
    /// Ray teleport handlers ///
    handleRayCurveTeleporterFacet(uid, facet) {
        const controllerUid = this.resolveControllerUid(uid, facet);
        if (controllerUid === undefined) {
            return;
        }
        let teleporterObject = this.teleporter.get(uid);
        if (teleporterObject === undefined) {
            teleporterObject = { teleporter: new Teleporter(), controllerUid };
            this.teleporter.set(uid, teleporterObject);
        }
        teleporterObject.controllerUid = controllerUid;
        teleporterObject.teleporter.setViewArrowYaw(facet.params.directionYaw);
        this.updateTeleporterTarget(uid, controllerUid);
        if (this.activeTeleporterUid !== undefined) {
            const activeTeleporterObject = this.teleporter.get(this.activeTeleporterUid);
            if (activeTeleporterObject !== undefined) {
                activeTeleporterObject.teleporter.setEnabled(false);
            }
        }
        teleporterObject.teleporter.updatedTime = Date.now();
        this.activeTeleporterUid = uid;
    }
    handleRemoveRayCurveTeleporterFacet(uid, _facet, meta) {
        const teleporter = this.teleporter.get(uid);
        if (teleporter === undefined)
            return;
        if (teleporter.teleporter.updatedTime > meta.timeStamp)
            return;
        this.deleteTeleporter(uid);
    }
    handleControllerMovedMessage(_msg) {
    }
    handleEnableTeleportMessage(msg) {
        if (msg.hand !== undefined && msg.hand !== ControllerHand.Unknown) {
            this.teleporterActivation.set(msg.hand, msg.enable);
        }
        else {
            this.teleporterActivation.set(ControllerHand.LeftHand, msg.enable);
            this.teleporterActivation.set(ControllerHand.RightHand, msg.enable);
        }
    }
    handleTeleportResultingViewDirectionMessage(msg) {
        if (this.isTeleporting)
            return;
        const teleporter = this.teleporter.get(msg.controllerUid);
        if (teleporter === undefined)
            return;
        teleporter.teleporter.setViewArrowYaw(msg.angle);
    }
    handleStartTeleportVrMessage(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            const teleporter = this.teleporter.get(msg.controllerUid);
            if (teleporter === undefined || this.activeTeleporterUid !== msg.controllerUid || !teleporter.teleporter.isEnabled()) {
                return;
            }
            const hitPoint = teleporter.teleporter.getHitPoint();
            if (hitPoint !== undefined) {
                this.isTeleporting = true;
                const viewArrowWorldYaw = teleporter.teleporter.getViewArrowWorldYaw();
                const target = tempTeleportTarget.copy(hitPoint);
                if (renderManager.isXRPresenting()) {
                    tempTeleportOffset.copy(camera.perspectiveCam.position);
                    tempTeleportOffset.y = 0;
                    tempTeleportOffset.multiplyScalar(localPlayerContainer.getPlatformScale());
                    if (viewArrowWorldYaw !== undefined) {
                        tempTeleportRotation.setFromAxisAngle(Y_AXIS, viewArrowWorldYaw);
                    }
                    else {
                        localPlayerContainer.getOrientation(tempTeleportRotation);
                    }
                    tempTeleportOffset.applyQuaternion(tempTeleportRotation);
                    target.sub(tempTeleportOffset);
                }
                else {
                    target.y += 1.7;
                }
                try {
                    yield this.connection.askByMessageType(new SetPlayerWorldTransformMessage({
                        position: convertToNumber3(target),
                        angle: viewArrowWorldYaw
                    }), DestinationReachedMessage);
                }
                catch (error) {
                    console.error(error);
                }
            }
            this.isTeleporting = false;
            this.activeTeleporterUid = undefined;
        });
    }
    /// Model event handlers ///
    handleRemoveSubModelMessage(msg, meta) {
        const teleporter = this.teleporter.get(msg.uid);
        if (teleporter === undefined)
            return;
        if (teleporter.teleporter.updatedTime > meta.timeStamp)
            return;
        this.deleteTeleporter(msg.uid);
    }
    handleFacetVisibility(uid, facet) {
        if (facet.facetType !== RayCurveTeleporterFacet.typestatic)
            return;
        const teleporter = this.teleporter.get(uid);
        if (teleporter === undefined)
            return;
        teleporter.teleporter.setEnabled(facet.visible);
        if (this.activeTeleporterUid !== undefined && this.activeTeleporterUid === uid && !facet.visible) {
            this.activeTeleporterUid = undefined;
        }
    }
    handleRenderVisibilityMessage(msg) {
        const teleporter = this.teleporter.get(msg.uid);
        if (teleporter === undefined)
            return;
        teleporter.teleporter.setEnabled(msg.visible);
        if (this.activeTeleporterUid !== undefined && this.activeTeleporterUid === msg.uid && !msg.visible) {
            this.activeTeleporterUid = undefined;
        }
    }
    handleControllerButtonMessage(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.teleporterActive && msg.uid !== this.localActiveTeleporterUid)
                return;
            // If teleport mode is not enabled, do nothing onButtonInput. In teleport mode the teleportation is handled onAxisInput.
            if (this.vrMovementMode === "teleport") {
                if (msg.buttonType === ButtonType.CLICK && msg.input === ButtonSignal.RELEASED) {
                    yield this.teleport();
                    this.removeTeleporterFromController();
                    this.teleporterActive = false;
                }
            }
            // If walk+teleport mode is enabled, handle teleport activation and teleportation on button A press and release.
            else if (this.vrMovementMode === "walk+teleport") {
                if (msg.buttonType === ButtonType.BUTTON_A) {
                    if (msg.input === ButtonSignal.PRESSED) {
                        this.activateTeleporter(msg.uid);
                    }
                    else if (msg.input === ButtonSignal.RELEASED) {
                        yield this.teleport();
                        this.removeTeleporterFromController();
                        this.teleporterActive = false;
                    }
                }
            }
        });
    }
    handleControllerAxisMessage(msg) {
        this.handleTeleportAxisInput(msg.uid, msg.value, msg.axisType);
    }
    /// Private teleporter methods ///
    /**
     * Executes a teleport to the currently active teleporter's target, if one is active, then deactivates it
     * and starts the reactivation cooldown.
     * @returns whether a teleport was actually performed.
     */
    teleport() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.teleporterActive)
                return false;
            if (this.localActiveTeleporterUid === undefined)
                return false;
            try {
                yield this.handleStartTeleportVrMessage(new StartTeleportVrMessage(this.localActiveTeleporterUid));
                this.removeTeleporterFromController();
                this.teleporterActive = false;
                this.nextTeleporterActivationAllowedAt = Date.now() + TELEPORTER_REACTIVATION_DELAY_MS;
                return true;
            }
            catch (error) {
                console.error(error);
                return false;
            }
        });
    }
    updateJoystickComponent(axisType, currentValue) {
        if (axisType === undefined)
            return;
        switch (axisType) {
            case AxisType.THUMBSTICK_X:
            case AxisType.TOUCHPAD_X:
                this.joyStickValue.x = currentValue;
                break;
            case AxisType.THUMBSTICK_Y:
            case AxisType.TOUCHPAD_Y:
                this.joyStickValue.y = currentValue;
                break;
        }
    }
    handleMovementModeChanged(newValue) {
        if (this.vrMovementMode === newValue)
            return;
        this.vrMovementMode = newValue;
        // Switching movement mode should immediately clear any active advanced
        // teleporter state so the old stick-driven visualization cannot remain
        // visible after entering basic movement mode.
        this.changeResultingTeleportDirection(undefined);
        this.removeTeleporterFromController();
        this.teleporterActive = false;
        this.activeTeleporterUid = undefined;
        this.lastTeleportAxisInputAt = 0;
        this.joyStickValue.set(0, 0);
    }
    canActivateTeleporter() {
        return (Date.now() >= this.nextTeleporterActivationAllowedAt || this.vrMovementMode === "walk+teleport");
    }
    enforceTeleporterInactivityTimeout() {
        var _a;
        if (!this.teleporterActive)
            return;
        const analogStickDeadZoneThreshold = (_a = preferencesService.getNumberPreferredValue("analogStickDeadZoneThreshold", 0.1)) !== null && _a !== void 0 ? _a : 0.1;
        if (this.joyStickValue.length() >= analogStickDeadZoneThreshold || this.vrMovementMode === "walk+teleport") {
            return;
        }
        if (Date.now() - this.lastTeleportAxisInputAt < TELEPORTER_INPUT_TIMEOUT_MS)
            return;
        console.info("[ControllerInputDevice] Teleporter deactivated due to inactivity");
        this.changeResultingTeleportDirection(undefined);
        this.removeTeleporterFromController();
        this.teleporterActive = false;
        this.lastTeleportAxisInputAt = 0;
    }
    handleTeleportAxisInput(controllerUid, currentValue, axisType) {
        var _a;
        if (axisType === undefined)
            return;
        if (this.vrMovementMode !== "teleport")
            return;
        this.updateJoystickComponent(axisType, currentValue);
        const analogStickDeadZoneThreshold = (_a = preferencesService.getNumberPreferredValue("analogStickDeadZoneThreshold", 0.1)) !== null && _a !== void 0 ? _a : 0.1;
        const joystickMagnitude = this.joyStickValue.length();
        const canActivateTeleporter = this.canActivateTeleporter();
        if (this.teleporterActive) {
            this.changeResultingTeleportDirection(undefined);
        }
        if (Math.abs(currentValue) > analogStickDeadZoneThreshold) {
            if (this.teleporterActive) {
                this.changeResultingTeleportDirection(Math.atan2(this.joyStickValue.y, -this.joyStickValue.x));
                this.lastTeleportAxisInputAt = Date.now();
            }
            else if (canActivateTeleporter) {
                this.activateTeleporter(controllerUid);
            }
            return;
        }
        if (this.teleporterActive && joystickMagnitude < analogStickDeadZoneThreshold) {
            this.changeResultingTeleportDirection(undefined);
        }
    }
    changeResultingTeleportDirection(delta) {
        console.info("[ControllerInputDevice] changeResultingTeleportDirection", delta);
        if (this.localActiveTeleporterUid === undefined)
            return;
        this.handleTeleportResultingViewDirectionMessage(new TeleportResultingViewDirectionMessage(this.localActiveTeleporterUid, delta));
    }
    removeTeleporterFromController() {
        const teleporterUid = this.localActiveTeleporterUid;
        if (teleporterUid === undefined)
            return;
        this.handleFacetVisibility(teleporterUid, new FacetVisibility(RayCurveTeleporterFacet, false));
        this.localActiveTeleporterUid = undefined;
    }
    activateTeleporter(controllerUid) {
        if (this.teleporterActive)
            return;
        if (!this.canActivateTeleporter())
            return;
        this.handleRayCurveTeleporterFacet(controllerUid, new RayCurveTeleporterFacet(controllerUid, { directionYaw: undefined }));
        this.handleFacetVisibility(controllerUid, new FacetVisibility(RayCurveTeleporterFacet, true));
        this.localActiveTeleporterUid = controllerUid;
        this.teleporterActive = true;
        this.lastTeleportAxisInputAt = Date.now();
        console.info("[ControllerInputDevice] Teleporter activated");
    }
    ensureTeleporterModelLink(uid, controllerUid) {
        if (uid === controllerUid)
            return;
        const teleporterModel = modelPool.getModel(uid);
        if (teleporterModel === undefined)
            return;
        const targetUid = getControllerPickupTargetUid(controllerUid);
        const existingLink = teleporterModel.getFacet(LinkToModelFacet);
        if (existingLink !== undefined) {
            const linkFacet = existingLink;
            if (linkFacet.targetUID === targetUid && hasSameOffset(linkFacet.offsetMatrix, TELEPORTER_LINK_OFFSET)) {
                return;
            }
        }
        if (teleporterModel.getFacet(LinkToModelFacet) !== undefined) {
            teleporterModel.removeFacet(LinkToModelFacet, NO_MESSAGING);
        }
        teleporterModel.setFacet(new LinkToModelFacet(targetUid, TELEPORTER_LINK_OFFSET), NO_MESSAGING);
    }
    updateTeleporterTarget(uid, controllerUid) {
        const teleporter = this.teleporter.get(uid);
        if (teleporter !== undefined) {
            teleporter.controllerUid = controllerUid;
        }
        this.ensureTeleporterModelLink(uid, controllerUid);
    }
    resolveControllerUid(uid, facet) {
        var _a, _b;
        if (typeof facet.controllerUid === "number") {
            return facet.controllerUid;
        }
        const existingLink = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getFacet(LinkToModelFacet);
        if (typeof (existingLink === null || existingLink === void 0 ? void 0 : existingLink.targetUID) === "number") {
            return existingLink.targetUID;
        }
        return (_b = this.teleporter.get(uid)) === null || _b === void 0 ? void 0 : _b.controllerUid;
    }
    updateTeleporterPreview() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.onUpdate();
            }
            finally {
                this.teleporterUpdateInProgress = false;
            }
        });
    }
    deleteTeleporter(uid) {
        const teleporter = this.teleporter.get(uid);
        if (teleporter === undefined)
            return;
        teleporter.teleporter.destroy();
        this.teleporter.delete(uid);
        if (this.localActiveTeleporterUid === uid) {
            this.localActiveTeleporterUid = undefined;
            this.teleporterActive = false;
        }
        if (this.activeTeleporterUid !== undefined && this.activeTeleporterUid === uid) {
            this.activeTeleporterUid = undefined;
        }
    }
}
__decorate([
    FacetHandler(RayCurveTeleporterFacet)
], TeleporterModule.prototype, "handleRayCurveTeleporterFacet", null);
__decorate([
    FacetRemoveHandler(RayCurveTeleporterFacet)
], TeleporterModule.prototype, "handleRemoveRayCurveTeleporterFacet", null);
__decorate([
    MessageHandler(ControllerMovedMessage)
], TeleporterModule.prototype, "handleControllerMovedMessage", null);
__decorate([
    MessageHandler(EnableTeleportMessage)
], TeleporterModule.prototype, "handleEnableTeleportMessage", null);
__decorate([
    MessageHandler(TeleportResultingViewDirectionMessage)
], TeleporterModule.prototype, "handleTeleportResultingViewDirectionMessage", null);
__decorate([
    MessageHandler(StartTeleportVrMessage)
], TeleporterModule.prototype, "handleStartTeleportVrMessage", null);
__decorate([
    MessageHandler(RemoveSubModelMessage)
], TeleporterModule.prototype, "handleRemoveSubModelMessage", null);
__decorate([
    FacetHandler(FacetVisibility)
], TeleporterModule.prototype, "handleFacetVisibility", null);
__decorate([
    MessageHandler(RenderVisibilityMessage)
], TeleporterModule.prototype, "handleRenderVisibilityMessage", null);
__decorate([
    MessageHandler(ControllerButtonMessage)
], TeleporterModule.prototype, "handleControllerButtonMessage", null);
__decorate([
    MessageHandler(ControllerAxisMessage)
], TeleporterModule.prototype, "handleControllerAxisMessage", null);
function hasSameOffset(a, b) {
    if (a === b)
        return true;
    if (a === undefined || b === undefined)
        return false;
    if (a.length !== b.length)
        return false;
    return a.every((value, index) => value === b[index]);
}
